import React from 'react';
import { Theme, Box, makeStyles, useTheme, useMediaQuery } from '@material-ui/core';
import CardSheet from './CardSheet';
import AvatarImage from './AvatarImage';
import EmailLink from './EmailLink';
import LinkedInLink from './LinkedInLink';
import EditPencil from './EditPencil';
import { ImageProvider } from '../types/types';
import { StyledText } from './StyledText';
import { optionalInfo, tbd } from '../lib/utils';
import DetectLinks from './DetectLinks';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  topDetails: {
    marginLeft: '52px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  separator: {
    height: '1px',
    width: '100%',
    backgroundColor: '#DAD9DF'
  },
  linksBox: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  summary: {
    whiteSpace: 'pre-wrap'
  },
  emailWrapper: {
    cursor: 'pointer',
    '& :hover p': {
      color: theme.palette.secondary.main
    }
  }
}));

interface ProfileCardProps {
  name: string,
  email: string,
  company: string,
  linkedIn?: string,
  summary: string,
  profileImageProvider: ImageProvider
  hideContactInfo?: boolean,
  usePlaceholders?: boolean
  editHandler?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  emailClickHandler?: () => void
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  name,
  profileImageProvider,
  email,
  company,
  linkedIn,
  summary,
  editHandler,
  emailClickHandler = undefined,
  hideContactInfo = false,
  usePlaceholders = false
}) => {
  const isMedium = useMediaQuery('(max-width: 600px)');
  const theme = useTheme();
  const classes = useStyles(theme);

  const contentPadding = editHandler ? '0px 55px 50px 55px' : '50px 55px 50px 55px';

  let summaryText = summary;
  if (!summaryText && usePlaceholders) {
    summaryText = tbd;
  }

  let linkedInText = linkedIn;
  if (!linkedInText && usePlaceholders) {
    linkedInText = tbd;
  }

  return (
    <CardSheet>
      <Box display={editHandler ? 'flex' : 'none'} marginTop='16px' marginRight='13px' justifyContent='flex-end'>
        <EditPencil onClick={editHandler} />
      </Box>
      <Box display='flex' flexDirection='column' padding={contentPadding}>
        <Box display='flex' flexDirection='row' marginBottom='49px'>
          <AvatarImage profileImageProvider={profileImageProvider} size={isMedium ? 80 : 185} />
          <Box className={classes.topDetails}>
            <StyledText variant='h2'>{name}</StyledText>
            <StyledText variant='subtitle1' color='primary'>{company}</StyledText>
            { !hideContactInfo
            && (
            <>
              <div className={classes.separator} style={{ margin: '15px 0px' }} />
              <Box className={classes.linksBox}>
                <Box className={emailClickHandler ? classes.emailWrapper : ''} onClick={emailClickHandler}>
                  <EmailLink email={email} clickable={false} marginRight='20px' />
                </Box>
                {linkedInText
                  ? <LinkedInLink linkedIn={linkedInText} placeholderMode={(linkedInText === tbd) || (linkedInText === optionalInfo)} />
                  : null }
              </Box>
            </>
            )}
          </Box>
        </Box>
        <StyledText className={classes.summary}>
          <DetectLinks content={summaryText} />
        </StyledText>
      </Box>
    </CardSheet>
  );
};

export default ProfileCard;
